'use strict';

(function () {
  'use strict';

  angular.module('msCore').service('msAuth', msAuth);

  msAuth.$inject = ['$q', '$log', '$cookies', '$http', '$sessionStorage', '$localStorage', 'windowSvc', 'domainSvc', 'authInterceptorSvc', '$window', '$rootScope', 'NotifyingSvc', '$timeout'];

  /**
   * This service deals with authenticated calls to the API.
   * This will behave as an $http interceptor that will add an auth header to all the auth'ed API requests,
   * and if it receives an updated header back it will update the cookie containing the token.
   */
  function msAuth($q, $log, $cookies, $http, $sessionStorage, $localStorage, windowSvc, domainSvc, authInterceptorSvc, $window, $rootScope, NotifyingSvc, $timeout) {
    var X_SESSION_TOKEN = 'x-session-token';
    var X_REAUTH_TOKEN = 'x-reauth-token';
    var X_SESSION_TOKEN_ISSUED = 'x-session-token-issued';
    var TIME_TO_OPEN_LOGOUT_MODAL = 3480000; // 58 minutes = 3 480 000 milliseconds

    // fetch the token from the cookie at the beginning
    var token = $cookies.get('token');
    var ssoLoginToken = null;
    var allowRefresh = true;
    var pingOneLogoutUrl;
    var isHTTPS = $window.location.protocol === 'https:' ? true : false;

    return {
      isLoggedIn: isLoggedIn,
      setToken: setToken,
      setReauthToken: setReauthToken,
      getReauthToken: getReauthToken,
      hasReauthCookie: hasReauthCookie,
      getPermissions: getPermissions,
      hasPermission: hasPermission,
      getSessionId: getSessionId,
      isWriteOnlyUser: isWriteOnlyUser,
      setSsoLogoutUrl: setSsoLogoutUrl,
      verifyTokenIssued: verifyTokenIssued,
      logout: logout,
      login: _login,
      loadSsoAuthInfo: _loadSsoAuthInfo,
      loadTeladocSsoAuthInfo: _loadTeladocSsoAuthInfo,
      loadKeycloakSsoAuthInfo: _loadKeycloakSsoAuthInfo,
      submitEmailVerificationRequest: _submitEmailVerificationRequest,
      ssoMapAndLogin: _ssoMapAndLogin,
      ssoLogin: _ssoLogin,
      signup: _signup,
      reauth: _reauth,
      oauth: _oauth,
      mfa: _mfa,
      clearCookieToken: clearCookieToken,
      ssoLogout: ssoLogout,
      partialLogout: partialLogout,
      getExpiration: getExpiration,
      clearSessionStorage: clearSessionStorage,
      clearLocalStorage: clearLocalStorage
    };

    /**
     * @return True if the user has a token and the token is not expired
     */
    function isLoggedIn() {
      return hasTokenCookie() && !tokenExpired();
    }

    function _login(user, timezone) {
      return $http.post('/apiproxy/public/user/login?tz=' + timezone, user).then(_verifyLoggedInUserResponse);
    }

    function _signup(user, userMeta, customFields, timezone) {
      // If isMobileWebView - add property for mobile isMobileSignUp
      // - Causes API to provide a reAuthToken in the response
      if (userMeta.isMobileWebView) {
        user.isMobileSignUp = true;
      }
      // Set GDPR flag on user
      if (userMeta.isSubjectToGDPR) {
        user.isSubjectToGDPR = true;
      }
      // Set user meta data - has opted into myStrength emails
      if (userMeta.hasOptedInToMyStrengthEmails) {
        user.hasOptedInToMyStrengthEmails = true;
      }

      // TODO access code needs to have slashes replaced
      // get all the custom signup responses
      var custom = customFields.map(function (field) {
        return {
          signUpFieldName: field.name,
          response: field.response,
          signUpFieldCollectionId: field.signUpFieldCollectionId
        };
      });
      user.customFields = {
        responses: custom
      };
      return $http.post('/apiproxy/public/user?tz=' + timezone, user).then(_verifyLoggedInUserResponse);
    }

    function _loadSsoAuthInfo(token, agent) {

      return $http.get('/apiproxy/public/sso/attributes?tokenid=' + token + '&agentid=' + agent).then(function (resp) {
        var headers = angular.isFunction(resp.headers) ? resp.headers() : resp.headers;
        ssoLoginToken = headers[X_SESSION_TOKEN];
        $sessionStorage.ssoAuthView = resp.data;
        // Clear cookie token - ssoLoginToken held in memory in this service
        clearCookieToken();
        return $sessionStorage.ssoAuthView;
      });
    }

    function _loadTeladocSsoAuthInfo(id) {

      return $http.get('/apiproxy/public/sso/teladoc/attributes?id=' + id).then(function (resp) {
        var headers = angular.isFunction(resp.headers) ? resp.headers() : resp.headers;
        ssoLoginToken = headers[X_SESSION_TOKEN];
        $sessionStorage.ssoAuthView = resp.data;
        // Clear cookie token - ssoLoginToken held in memory in this service
        clearCookieToken();
        return $sessionStorage.ssoAuthView;
      });
    }

    function _loadKeycloakSsoAuthInfo(token) {
      return $http.get('/apiproxy/public/keyCloak/sso/attributes?tokenid=' + token).then(function (resp) {
        var headers = angular.isFunction(resp.headers) ? resp.headers() : resp.headers;
        ssoLoginToken = headers[X_SESSION_TOKEN];
        $sessionStorage.ssoAuthView = resp.data;
        // Clear cookie token - ssoLoginToken held in memory in this service
        clearCookieToken();
        return $sessionStorage.ssoAuthView;
      });
    }

    function _ssoLogin(ssoAuthView) {
      var config = {
        headers: {}
      };
      config.headers[X_SESSION_TOKEN] = ssoLoginToken;
      return $http.post('/apiproxy/public/sso/login', ssoAuthView, config).then(_verifyLoggedInUserResponse);
    }

    function _submitEmailVerificationRequest(ssoAuthView) {
      return $http.post('/apiproxy/public/sso/verify/email', ssoAuthView);
    }

    function _ssoMapAndLogin(ssoIdView) {
      logout();
      return $http.post('/apiproxy/public/sso/login/map', ssoIdView).then(_verifyLoggedInUserResponse);
    }

    function _reauth(timezone) {
      if (hasReauthCookie()) {
        var reauthView = {
          reauthToken: getReauthToken()
        };
        return $http.post('/apiproxy/public/reauth/web?tz=' + timezone, reauthView).then(_verifyLoggedInUserResponse, function (err) {
          $log.error(err);
          $cookies.remove('reauth_token', {
            domain: domainSvc.getRootDomain(),
            path: '/'
          });
        });
      }
      return $q.reject();
    }

    function _oauth(ticket) {
      // This response has two forms:
      // 1. returns a successful login with a JWT
      // 2. Returns a shell of a user signup to seed the signup process
      return $http.get('/apiproxy/public/oauth/authenticate?ticket=' + ticket).then(function (resp) {
        if (resp.data.type === 'LOGIN') {
          var respData = resp.data;
          // _verifyLoggedInUserResponse expects a response with the loginView as the data
          resp.data = resp.data.loginView;
          var validatedResponse = _verifyLoggedInUserResponse(resp);
          if (!validatedResponse.error) {
            validatedResponse.data = respData;
          }
          return validatedResponse;
        } else if (resp.data.type === 'SIGNUP') {
          return resp;
        } else {
          throw 'Unexpected response from server';
        }
      });
    }

    /**
     * Verify a multi-factor auth code for an already-authenticated user
     * @param authCode A second-factor authorization code provided by the user
     * @returns {HttpPromise} A promise which will resolve to a full HttpRequest
     */
    function _mfa(authCode) {
      return $http.post('/apiproxy/user/multifactor/verify', authCode, {
        withCredentials: true
      }).then(_verifyLoggedInUserResponse);
    }

    function _verifyLoggedInUserResponse(response) {
      var jwtToken = response.headers(X_SESSION_TOKEN);
      var issued = response.headers(X_SESSION_TOKEN_ISSUED);
      var reauthToken = response.headers(X_REAUTH_TOKEN);
      var authResponse = response.data;

      if (!jwtToken) {
        return {
          error: {
            message: 'An unknown error has occurred',
            contact: true
          }
        };
      }
      if (!setToken(jwtToken, true) || !setReauthToken(reauthToken)) {
        return {
          error: {
            message: 'Cookies must be enabled to use myStrength'
          }
        };
      }
      // If login returns pwExpired skip ahead to pw change redirect
      if (authResponse.pwExpired) {
        return response;
      }
      if (!verifyTokenIssued(issued)) {
        return {
          error: {
            message: 'Your computer\'s time appears incorrect - please verify it and try again'
          }
        };
      }
      return response;
    }

    /**
     * Remove the user auth token cookie if present.
     */
    function logout() {
      token = null;
      allowRefresh = true;
      return clearCookie();
    }

    /**
     * Clear the session cookie
     * @return {boolean} True if able to clear the cookie, else false
     */
    function clearCookie() {
      $cookies.remove('token', {
        domain: domainSvc.getRootDomain(),
        path: '/'
      });
      $cookies.remove('reauth_token', {
        domain: domainSvc.getRootDomain(),
        path: '/'
      });
      //clear the student platform thrive cookie on logout
      $cookies.remove('thrive_seen', {
        domain: domainSvc.getRootDomain(),
        path: '/'
      });

      //clear the local storage session as well
      clearSessionStorage();
      // make sure we can't get the cookie
      return !$cookies.get('token');
    }

    /**
     * Clears only the cookie token leaving sessionStorage intact
     */
    function clearCookieToken() {
      $cookies.remove('token', {
        domain: domainSvc.getRootDomain(),
        path: '/'
      });
      // make sure we can't get the cookie
      return !$cookies.get('token');
    }

    function clearSessionStorage() {
      $sessionStorage.$reset();
    }

    function clearLocalStorage() {
      $localStorage.$reset();
    }

    function setSsoLogoutUrl(url) {
      if (url) {
        $log.debug('Setting the SSO Ping One logout URL to ' + url);
        pingOneLogoutUrl = url;
      }
    }

    /**
     * Perform an SSO logout for the IDp by taking the user there.
     * @return {boolean} True if performing SSO logout, else false
     */
    function ssoLogout() {
      var ssoIdpLogoutUrl = $cookies.get('ssoIdpLogoutUrl');
      if (ssoIdpLogoutUrl && ssoIdpLogoutUrl !== 'null' && pingOneLogoutUrl) {
        //drop the cookie since we'll have completed its purpose
        $cookies.remove('ssoIdpLogoutUrl', {
          domain: domainSvc.getAppDomain(),
          path: '/v/logout'
        });
        var ssoLogoutUrl = pingOneLogoutUrl + encodeURIComponent(ssoIdpLogoutUrl);
        $log.debug('Redirecting to PingOne Logout with an SSO IDP redirect URL  - ' + ssoLogoutUrl);
        windowSvc.windowNavigate(ssoLogoutUrl);
        return true;
      } else {
        return false;
      }
    }

    /**
     * Remove the token cookie, so that if the page is refreshed or closed the user will be logged out the rest of the way.
     * This is to allow the conclusion page to view the tracker and send feedback without the user being all the way logged in.
     */
    function partialLogout() {
      allowRefresh = false;
      clearCookie();
    }

    /**
     * Get the expiration of the current token
     * @param  {string} jwt (Optional) Web token to check for expiration
     * @return {Date}                  The time when the token will expire
     */
    function getExpiration(jwt) {
      jwt = jwt || token;
      if (!jwt) {
        return null;
      }
      try {
        // split the JWT into three chunks of base64 - decode the middle chunck, parse it as JSON
        // and extract the .exp value then multiply by 1000 to get MS. Make a date from it.
        return new Date(JSON.parse(atob(jwt.split('.')[1])).exp * 1000);
      } catch (e) {
        return null;
      }
    }
    /**
     * @param  {string} jwt (Optional) Web token to check for expiration
     * @return {boolean}               True if the token is expired
     */
    function tokenExpired(jwt) {
      return getExpiration(jwt) < new Date();
    }

    /**
     * Get user permissions from token passed in or from previously stored token
     * @param {string} jwt (Optional) web token to check
     * @return null if token doesn't exist
     *         else, return array of available permissions
     */
    function getSessionId(jwt) {
      jwt = jwt || token;
      if (!jwt) {
        return null;
      }

      try {
        var sessionId = JSON.parse(atob(jwt.split('.')[1])).sessionId;
        if (!tokenExpired(jwt) && sessionId) {
          return sessionId;
        }
      } catch (e) {
        return null;
      }
      return null;
    }

    /**
     * Get user permissions from token passed in or from previously stored token
     * @param {string} jwt (Optional) web token to check
     * @return null if token doesn't exist
     *         else, return array of available permissions
     */
    function getPermissions(jwt) {
      jwt = jwt || token;
      if (!jwt) {
        return [];
      }

      try {
        var perms = JSON.parse(atob(jwt.split('.')[1])).permissions;
        if (!tokenExpired(jwt) && angular.isArray(perms)) {
          return perms;
        }
      } catch (e) {
        return [];
      }
      return [];
    }

    function hasPermission(permissionName) {
      return getPermissions(null).includes(permissionName);
    }

    /**
     * @param  {string} issued  the JWT issue date to verify against the local time.
     * @return {boolean}               true if the issued time is within timeout range.
     */
    function verifyTokenIssued(issued) {
      var parsedIssueDate = new Date(parseInt(issued)),
          parsedUpperBound = new Date(new Date().getTime() + 600000),
          parsedLowerBound = new Date(new Date().getTime() - 600000);
      return parsedIssueDate <= parsedUpperBound && parsedIssueDate >= parsedLowerBound;
    }
    /**
     * Update the token. This will also store the token in the cookie to ensure it survives a page refresh.
     * @param  {string}  jwt The new token
     * @param  {boolean} forceTokenRefresh if true will force a token refresh
     * @return {boolean} true if the token is set successfully, false if not
     */
    function setToken(jwt, forceTokenRefresh) {
      if (jwt && allowRefresh && (shouldRefreshToken() || forceTokenRefresh)) {
        token = jwt;
        $cookies.put('token', jwt, {
          domain: domainSvc.getRootDomain(),
          path: '/',
          expires: getExpiration(jwt),
          secure: isHTTPS
        });
        authInterceptorSvc.refreshTokenFromCookie();
        // make sure we can retrieve the token
        return $cookies.get('token') === jwt;
      }

      return true;
    }

    /**
     * Fetches the the Reauth token from the cookie
     */
    function getReauthToken() {
      return $cookies.get('reauth_token');
    }

    /**
     * Update or set the reauth token cookie. This will store the reauth token in a cookie to ensure it survives a page refresh.
     * @param  {string}  jwt The new reauth token
     * @return {boolean} true if the token is set successfully, false if not
     */
    function setReauthToken(jwt) {
      if (jwt) {
        $cookies.put('reauth_token', jwt, {
          domain: domainSvc.getRootDomain(),
          path: '/',
          expires: getExpiration(jwt),
          secure: isHTTPS
        });

        if (!$rootScope.isMobileWebView) {
          $timeout.cancel($rootScope.reauthTimer);
          $rootScope.reauthTimer = $timeout(function () {
            NotifyingSvc.notifyEvent(NotifyingSvc.EVENTS.OPEN_MODAL, {
              templateUrl: 'core/templates/logout-modal.html',
              controller: 'LogoutModalCtrl',
              inputs: {}
            });
          }, TIME_TO_OPEN_LOGOUT_MODAL);
        }
        // make sure we can retrieve the token
        return $cookies.get('reauth_token') === jwt;
      }

      return true;
    }

    /**
     * @return {boolean} true if token cookie exists
     */
    function hasTokenCookie() {
      return !!token || !_.isEmpty($cookies.get('token'));
    }

    /**
     * @return {boolean} true if the reauth cookie exists
     */
    function hasReauthCookie() {
      return !_.isEmpty($cookies.get('reauth_token'));
    }

    /**
     * @return True if user is not logged in but has token giving write-only permission
     **/
    function isWriteOnlyUser() {
      return _.includes(getPermissions(), 'USERS_WRITE_ONLY') || _.includes(getPermissions(), 'PASSWORD_RESET');
    }

    function shouldRefreshToken() {
      var tokenExpireDate = getExpiration();
      var tokenExpireTimestamp = tokenExpireDate !== null ? tokenExpireDate.getTime() : 0;
      var tokenCanRefreshTimestamp = new Date().getTime() + 300000; // 5min

      // Update token if not yet set OR expires within 5 min
      if (tokenExpireDate === null || tokenExpireTimestamp <= tokenCanRefreshTimestamp) {
        return true;
      }

      return false;
    }
  }
})();